import { useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import { mailApi, type ImapConnectArgs } from '../../services/mailApi'

// OSPROD-MAIL-CAL-L3: classic IMAP/SMTP credential form. On submit the
// Node route forwards to Python, which actually attempts the
// connection and returns a structured error (AUTH_FAILED, TLS_ERROR,
// HOST_UNREACHABLE, TIMEOUT). Error messages are surfaced inline so
// the user can correct without bouncing through a toast.

interface Props {
  onClose: () => void
  onConnected: (accountId: number) => void
}

export default function ImapConnectModal({ onClose, onConnected }: Props) {
  const [args, setArgs] = useState<ImapConnectArgs>({
    email: '',
    host: '',
    port: 993,
    user: '',
    password: '',
    secure: 'tls',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const update = <K extends keyof ImapConnectArgs>(k: K, v: ImapConnectArgs[K]) =>
    setArgs((prev) => ({ ...prev, [k]: v }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const res = await mailApi.imapConnect(args)
      onConnected(res.data.accountId)
      onClose()
    } catch (err: any) {
      const code = err?.response?.data?.code
      const message = err?.response?.data?.error
      const friendly =
        code === 'AUTH_FAILED' ? 'Username or password is incorrect.' :
        code === 'TLS_ERROR' ? 'TLS/STARTTLS handshake failed. Try a different secure mode.' :
        code === 'HOST_UNREACHABLE' ? 'Could not reach that host. Double-check the address and port.' :
        code === 'TIMEOUT' ? 'The server took too long to respond.' :
        message || 'Failed to connect.'
      setError(friendly)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="bg-card rounded-xl shadow-2xl w-[420px] max-w-[95vw]"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-foreground font-semibold">Add IMAP / SMTP account</h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Email">
            <input
              type="email" required value={args.email}
              onChange={(e) => update('email', e.target.value)}
              className="w-full bg-muted rounded px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
            />
          </Field>
          <div className="grid grid-cols-[1fr_120px] gap-2">
            <Field label="Host">
              <input
                required value={args.host}
                onChange={(e) => update('host', e.target.value)}
                placeholder="imap.example.com"
                className="w-full bg-muted rounded px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
              />
            </Field>
            <Field label="Port">
              <input
                type="number" required value={args.port}
                onChange={(e) => update('port', Number(e.target.value))}
                className="w-full bg-muted rounded px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
              />
            </Field>
          </div>
          <Field label="Username">
            <input
              required value={args.user}
              onChange={(e) => update('user', e.target.value)}
              className="w-full bg-muted rounded px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
            />
          </Field>
          <Field label="Password">
            <input
              type="password" required value={args.password}
              onChange={(e) => update('password', e.target.value)}
              className="w-full bg-muted rounded px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
            />
          </Field>
          <Field label="Encryption">
            <select
              value={args.secure}
              onChange={(e) => update('secure', e.target.value as 'tls' | 'starttls' | 'none')}
              className="w-full bg-muted rounded px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="tls">TLS (port 993)</option>
              <option value="starttls">STARTTLS (port 143)</option>
              <option value="none">None (insecure)</option>
            </select>
          </Field>
          {error && (
            <div className="text-xs text-red-400 bg-red-500/10 rounded px-3 py-2">{error}</div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-card">
          <button
            type="button" onClick={onClose} disabled={submitting}
            className="px-4 py-2 text-sm rounded-md text-muted-foreground hover:text-foreground transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit" disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      </form>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </label>
  )
}
